<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { FunnelIcon } from '@heroicons/vue/24/outline';
import { onClickOutside } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { IMaintenance } from '@/interfaces/entities/maintenance';

export interface IMaintenanceSectionFilters {
  userId: number | null;
  priority: string | null;
  sensorId: number | null;
}

const emptyFilters = (): IMaintenanceSectionFilters => ({
  userId: null,
  priority: null,
  sensorId: null,
});

const props = defineProps<{
  maintenances: IMaintenance[];
  modelValue: IMaintenanceSectionFilters;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: IMaintenanceSectionFilters): void;
}>();

const { t } = useI18n();

const isOpen = ref(false);
const panelRef = ref<HTMLElement | null>(null);
const draft = ref<IMaintenanceSectionFilters>(emptyFilters());

onClickOutside(panelRef, () => {
  isOpen.value = false;
});

watch(isOpen, (open) => {
  if (open) {
    draft.value = { ...props.modelValue };
  }
});

const userOptions = computed(() => {
  const map = new Map<number, string>();
  for (const maintenance of props.maintenances) {
    if (maintenance.user?.id != null && maintenance.user.name) {
      map.set(maintenance.user.id, maintenance.user.name);
    }
  }
  return Array.from(map.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
});

const sensorOptions = computed(() => {
  const map = new Map<number, string>();
  for (const maintenance of props.maintenances) {
    if (maintenance.sensor?.id != null && maintenance.sensor.name) {
      map.set(maintenance.sensor.id, maintenance.sensor.name);
    }
  }
  return Array.from(map.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
});

const priorityOptions = computed(() =>
  (['high', 'medium', 'low'] as const).map((id) => ({
    id,
    name: t(`maintenance_user_listing.priority.${id}`),
  })),
);

const appliedCount = computed(() => {
  const filters = props.modelValue;
  return [filters.userId, filters.priority, filters.sensorId].filter((value) => value != null && value !== '').length;
});

function togglePanel() {
  isOpen.value = !isOpen.value;
}

function applyFilters() {
  emit('update:modelValue', { ...draft.value });
  isOpen.value = false;
}

function clearFilters() {
  draft.value = emptyFilters();
  emit('update:modelValue', emptyFilters());
  isOpen.value = false;
}

function onUserChange(event: Event) {
  const value = (event.target as HTMLSelectElement).value;
  draft.value.userId = value === '' ? null : Number(value);
}

function onPriorityChange(event: Event) {
  const value = (event.target as HTMLSelectElement).value;
  draft.value.priority = value === '' ? null : value;
}

function onSensorChange(event: Event) {
  const value = (event.target as HTMLSelectElement).value;
  draft.value.sensorId = value === '' ? null : Number(value);
}
</script>

<template>
  <div ref="panelRef" class="relative ml-auto shrink-0">
    <button
      type="button"
      class="relative flex h-7 w-7 items-center justify-center text-slate-600 transition hover:bg-slate-200 hover:text-slate-800"
      :aria-label="t('maintenance_user_listing.body.filters.title')"
      :title="t('maintenance_user_listing.body.filters.title')"
      :aria-expanded="isOpen"
      @click.stop="togglePanel"
    >
      <FunnelIcon class="h-5 w-5" aria-hidden="true" />
      <span
        v-if="!isOpen && appliedCount > 0"
        class="absolute -right-1 -top-1 flex h-4 min-w-[1rem] bg-blue-100 bg-opacity-50 rounded-full items-center justify-center px-1 text-[12px] font-bold leading-none text-blue"
      >
        {{ appliedCount }}
      </span>
    </button>

    <div
      v-show="isOpen"
      class="absolute right-0 top-full z-20 mt-1 w-56 border border-stroke bg-white p-3 shadow-lg"
      @click.stop
    >
      <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {{ t('maintenance_user_listing.body.filters.title') }}
      </p>

      <div class="flex flex-col gap-2.5">
        <label class="flex flex-col gap-1">
          <span class="text-xs font-medium text-slate-700">
            {{ t('maintenance_user_listing.body.filters.user') }}
          </span>
          <select
            class="w-full border border-stroke bg-white px-2 py-1.5 text-sm text-slate-700 outline-none focus:border-blue"
            :value="draft.userId ?? ''"
            @change="onUserChange"
          >
            <option value="">
              {{ t('maintenance_user_listing.body.filters.all') }}
            </option>
            <option v-for="option in userOptions" :key="option.id" :value="option.id">
              {{ option.name }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-xs font-medium text-slate-700">
            {{ t('maintenance_user_listing.body.filters.priority') }}
          </span>
          <select
            class="w-full border border-stroke bg-white px-2 py-1.5 text-sm text-slate-700 outline-none focus:border-blue"
            :value="draft.priority ?? ''"
            @change="onPriorityChange"
          >
            <option value="">
              {{ t('maintenance_user_listing.body.filters.all') }}
            </option>
            <option v-for="option in priorityOptions" :key="option.id" :value="option.id">
              {{ option.name }}
            </option>
          </select>
        </label>

        <label class="flex flex-col gap-1">
          <span class="text-xs font-medium text-slate-700">
            {{ t('maintenance_user_listing.body.filters.sensor') }}
          </span>
          <select
            class="w-full border border-stroke bg-white px-2 py-1.5 text-sm text-slate-700 outline-none focus:border-blue"
            :value="draft.sensorId ?? ''"
            @change="onSensorChange"
          >
            <option value="">
              {{ t('maintenance_user_listing.body.filters.all') }}
            </option>
            <option v-for="option in sensorOptions" :key="option.id" :value="option.id">
              {{ option.name }}
            </option>
          </select>
        </label>
      </div>

      <div class="mt-3 flex items-center justify-between gap-2 border-t border-stroke pt-3">
        <button
          type="button"
          class="px-2 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-800"
          @click="clearFilters"
        >
          {{ t('maintenance_user_listing.body.filters.clear') }}
        </button>
        <button
          type="button"
          class="bg-blue px-2.5 py-1.5 text-xs font-medium text-white transition hover:bg-blue-2"
          @click="applyFilters"
        >
          {{ t('maintenance_user_listing.body.filters.apply') }}
        </button>
      </div>
    </div>
  </div>
</template>
